import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { usersApi, UserGroupNode, GroupedUsersParams } from '../../../api/usersApi';
import { departmentsApi } from '../../../api/departmentsApi';
import { rolesApi } from '../../../api/rolesApi';
import { User } from '../../../types';
import { downloadBlob } from '../../../utils/helpers';
import {
  Search,
  Download,
  FileSpreadsheet,
  Refresh,
  ChevronRight,
  ChevronDown,
  X,
  Filter,
  StarFilled,
  Columns,
} from '../Icons';
import { Button } from '../Button';
import { FavoritesDropdown } from '../FavoritesDropdown';
import { FavoriteConfig } from '../../../api/favoritesApi';
import { ColumnSearchBar, SearchChip } from '../ColumnSearchBar';
import './GroupedEmployees.css';

/* ── Column Definitions ── */
interface ColumnConfig {
  key: string;
  label: string;
  defaultVisible: boolean;
}

const EMPLOYEE_COLUMNS: ColumnConfig[] = [
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'email', label: 'Email', defaultVisible: true },
  { key: 'employee_code', label: 'Employee Code', defaultVisible: true },
  { key: 'department', label: 'Department', defaultVisible: true },
  { key: 'manager', label: 'Manager', defaultVisible: true },
  { key: 'job_position', label: 'Job Position', defaultVisible: false },
  { key: 'category', label: 'Category', defaultVisible: false },
  { key: 'roles', label: 'Roles', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'sub_department', label: 'Sub Department', defaultVisible: false },
  { key: 'designation', label: 'Designation', defaultVisible: false },
  { key: 'location', label: 'Location', defaultVisible: false },
  { key: 'date_of_join', label: 'Date of Join', defaultVisible: false },
  { key: 'last_login', label: 'Last Login', defaultVisible: false },
  { key: 'practice', label: 'Practice', defaultVisible: false },
  { key: 'grade', label: 'Grade', defaultVisible: false },
  { key: 'band', label: 'Band', defaultVisible: false },
  { key: 'mobile', label: 'Mobile', defaultVisible: false },
];

const DEFAULT_VISIBLE_COLUMNS = EMPLOYEE_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);
const STORAGE_KEY_EMPLOYEE_COLUMNS = 'ge_visible_columns';

const EMPLOYEE_SEARCH_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'employee_code', label: 'Employee Code' },
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'role', label: 'Roles' },
];

/* ── Group By options ── */
type GroupByField = 'department' | 'manager' | 'role' | 'category' | 'practice' | 'status';

interface GroupByOption {
  key: GroupByField;
  label: string;
}

const GROUP_BY_OPTIONS: GroupByOption[] = [
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'role', label: 'Role' },
  { key: 'category', label: 'Category' },
  { key: 'practice', label: 'Practice' },
  { key: 'status', label: 'Status' },
];

function getGroupByLabel(key: GroupByField): string {
  return GROUP_BY_OPTIONS.find((o) => o.key === key)?.label ?? key;
}

/* ── Props ── */
interface GroupedEmployeesProps {
  pageTitle: string;
}

/* ── Component ── */
export function GroupedEmployees({ pageTitle }: GroupedEmployeesProps) {
  // Group-by state
  const [activeGroupBy, setActiveGroupBy] = useState<GroupByField[]>([]);
  const [groups, setGroups] = useState<UserGroupNode[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Detail rows
  const [detailRows, setDetailRows] = useState<Record<string, { users: User[]; total: number }>>({});
  const [detailLoading, setDetailLoading] = useState<Set<string>>(new Set());

  // Filters
  const [searchChips, setSearchChips] = useState<SearchChip[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [practiceFilter, setPracticeFilter] = useState('');
  const [activeInactiveFilter, setActiveInactiveFilter] = useState('true');
  const [showFilters, setShowFilters] = useState(false);

  // Active favorite
  const [activeFavoriteName, setActiveFavoriteName] = useState<string | null>(null);

  // Filter data
  const [filterDepartments, setFilterDepartments] = useState<{ id: number; name: string }[]>([]);
  const [filterManagers, setFilterManagers] = useState<{ id: number; name: string }[]>([]);
  const [filterRoles, setFilterRoles] = useState<{ name: string; display_name: string }[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterPractices, setFilterPractices] = useState<string[]>([]);

  // Export
  const [exportLoading, setExportLoading] = useState(false);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_EMPLOYEE_COLUMNS);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_VISIBLE_COLUMNS;
      }
    }
    return DEFAULT_VISIBLE_COLUMNS;
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement>(null);

  // Dropdowns
  const [showGroupByMenu, setShowGroupByMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const groupByMenuRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Flat view
  const [flatUsers, setFlatUsers] = useState<User[]>([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPage, setFlatPage] = useState(1);
  const [flatPages, setFlatPages] = useState(1);
  const PER_PAGE = 50;

  // Close menus on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (groupByMenuRef.current && !groupByMenuRef.current.contains(e.target as Node)) {
        setShowGroupByMenu(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) {
        setShowColumnMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save visible columns to localStorage when changed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_EMPLOYEE_COLUMNS, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Column toggle handlers
  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setVisibleColumns(EMPLOYEE_COLUMNS.map(c => c.key));
  };

  const handleResetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  // Available options
  const availableOptions = useMemo(
    () => GROUP_BY_OPTIONS.filter((o) => !activeGroupBy.includes(o.key)),
    [activeGroupBy],
  );

  /* ── Load filter data ── */
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [deptRes, rolesRes, mgrRes, usersRes] = await Promise.all([
          departmentsApi.getDepartments(),
          rolesApi.getRoles(),
          usersApi.getManagers(),
          usersApi.getUsers({ per_page: 500, is_active: 'true' }),
        ]);
        setFilterDepartments(
          (deptRes as any).departments?.map((d: any) => ({ id: d.id, name: d.name })) ??
            (Array.isArray(deptRes) ? (deptRes as any).map((d: any) => ({ id: d.id, name: d.name })) : []),
        );
        setFilterRoles(
          (rolesRes as any).roles?.map((r: any) => ({ name: r.name, display_name: r.display_name })) ?? [],
        );
        setFilterManagers(
          Array.isArray(mgrRes)
            ? (mgrRes as any).map((m: any) => ({ id: m.id, name: m.name }))
            : (mgrRes as any).managers?.map((m: any) => ({ id: m.id, name: m.name })) ?? [],
        );
        const users = (usersRes as any).users ?? [];
        setFilterCategories(Array.from(new Set(users.map((u: any) => u.category).filter(Boolean))) as string[]);
        setFilterPractices(Array.from(new Set(users.map((u: any) => u.practice).filter(Boolean))) as string[]);
      } catch {
        // silently fail
      }
    };
    loadFilterData();
  }, []);

  /* ── Build filter params ── */
  const buildFilterParams = useCallback(() => {
    const params: any = {};
    const colSearches: Record<string, string[]> = {};
    searchChips.forEach(chip => {
      if (!colSearches[chip.field]) colSearches[chip.field] = [];
      colSearches[chip.field].push(chip.value);
    });
    Object.entries(colSearches).forEach(([field, values]) => {
      params[`search_${field}`] = values.join('||');
    });
    if (departmentFilter) params.department_id = Number(departmentFilter);
    if (managerFilter) params.manager_id = Number(managerFilter);
    if (roleFilter) params.role = roleFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (practiceFilter) params.practice = practiceFilter;
    if (activeInactiveFilter) params.is_active = activeInactiveFilter;
    return params;
  }, [searchChips, departmentFilter, managerFilter, roleFilter, categoryFilter, practiceFilter, activeInactiveFilter]);

  /* ── Fetch grouped data ── */
  const fetchGroupedData = useCallback(async () => {
    if (activeGroupBy.length === 0) return;
    try {
      setLoading(true);
      setError(null);
      const params: GroupedUsersParams = {
        group_by: activeGroupBy.join(','),
        ...buildFilterParams(),
      };
      const res = await usersApi.getUsersGrouped(params);
      setGroups(res.groups);
      setTotalCount(res.total);
      setExpandedGroups(new Set());
      setDetailRows({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeGroupBy, buildFilterParams]);

  /* ── Fetch flat data ── */
  const fetchFlatData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const res = await usersApi.getUsers({
        page,
        per_page: PER_PAGE,
        ...buildFilterParams(),
      });
      setFlatUsers(res.users);
      setFlatTotal(res.total || 0);
      setFlatPage(res.current_page ?? 1);
      setFlatPages(res.pages ?? 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  /* ── Trigger fetch ── */
  useEffect(() => {
    if (activeGroupBy.length > 0) {
      fetchGroupedData();
    } else {
      fetchFlatData(1);
    }
    // eslint-disable-next-line
  }, [activeGroupBy, searchChips, departmentFilter, managerFilter, roleFilter, categoryFilter, practiceFilter, activeInactiveFilter]);

  /* ── Add/remove group by ── */
  const addGroupByField = (field: GroupByField) => {
    setActiveGroupBy((prev) => [...prev, field]);
    setShowGroupByMenu(false);
  };

  const removeGroupByField = (field: GroupByField) => {
    setActiveGroupBy((prev) => {
      const idx = prev.indexOf(field);
      if (idx === -1) return prev;
      return prev.slice(0, idx);
    });
  };

  /* ── Toggle expand ── */
  const toggleExpand = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /* ── Load detail rows ── */
  const loadDetailRows = useCallback(
    async (groupPath: string, filterOverrides: Record<string, any>) => {
      setDetailLoading((prev) => new Set(prev).add(groupPath));
      try {
        const res = await usersApi.getUsers({
          ...buildFilterParams(),
          ...filterOverrides,
          per_page: 500,
        });
        setDetailRows((prev) => ({
          ...prev,
          [groupPath]: { users: res.users, total: res.users.length },
        }));
      } catch {
        // silently fail
      } finally {
        setDetailLoading((prev) => {
          const next = new Set(prev);
          next.delete(groupPath);
          return next;
        });
      }
    },
    [buildFilterParams],
  );

  /* ── Build filter overrides from group node ── */
  const buildFilterOverrides = (node: UserGroupNode, parentOverrides: Record<string, any> = {}): Record<string, any> => {
    const overrides = { ...parentOverrides };
    if (node.field === 'department') overrides.department_id = node.field_value !== 'none' ? Number(node.field_value) : undefined;
    else if (node.field === 'manager') overrides.manager_id = node.field_value !== 'none' ? Number(node.field_value) : undefined;
    else if (node.field === 'role') overrides.role = node.field_value !== 'none' ? node.field_value : undefined;
    else if (node.field === 'category') overrides.category = node.field_value !== 'none' ? node.field_value : undefined;
    else if (node.field === 'practice') overrides.practice = node.field_value !== 'none' ? node.field_value : undefined;
    else if (node.field === 'status') overrides.is_active = node.field_value === 'true' || node.field_value === '1' ? 'true' : 'false';
    return overrides;
  };



  /* ── Export ── */
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const blob = await usersApi.exportUsers({
        format: 'xlsx',
        ...buildFilterParams(),
      });
      downloadBlob(blob, `employees_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      // ignore
    } finally {
      setExportLoading(false);
      setShowExportMenu(false);
    }
  };

  /* ── Render user row ── */
  const renderUserRow = (u: User) => (
    <tr key={u.id} className="ge-detail-row">
      {visibleColumns.includes('name') && (
        <td>
          <div className="ge-user-info">
            <div className="ge-user-avatar">{u.name.charAt(0).toUpperCase()}</div>
            <span>{u.name}</span>
          </div>
        </td>
      )}
      {visibleColumns.includes('email') && <td>{u.email}</td>}
      {visibleColumns.includes('employee_code') && <td>{u.employee_code || '-'}</td>}
      {visibleColumns.includes('department') && <td>{u.department || '-'}</td>}
      {visibleColumns.includes('manager') && <td>{u.manager || '-'}</td>}
      {visibleColumns.includes('job_position') && <td>{(u as any).job_position || '-'}</td>}
      {visibleColumns.includes('category') && <td>{(u as any).category || '-'}</td>}
      {visibleColumns.includes('roles') && (
        <td>
          <div className="ge-role-badges">
            {u.roles.map((r) => (
              <span key={r} className="ge-role-badge">{r.charAt(0).toUpperCase() + r.slice(1)}</span>
            ))}
          </div>
        </td>
      )}
      {visibleColumns.includes('status') && (
        <td>
          <span className={`ge-status-badge ${u.is_active ? 'ge-status-active' : 'ge-status-inactive'}`}>
            {u.is_active ? 'Active' : 'Inactive'}
          </span>
        </td>
      )}
      {visibleColumns.includes('sub_department') && <td>{(u as any).sub_department || '-'}</td>}
      {visibleColumns.includes('designation') && <td>{(u as any).designation || '-'}</td>}
      {visibleColumns.includes('location') && <td>{(u as any).location || '-'}</td>}
      {visibleColumns.includes('date_of_join') && <td>{(u as any).date_of_join || '-'}</td>}
      {visibleColumns.includes('last_login') && <td>{(u as any).last_login || '-'}</td>}
      {visibleColumns.includes('practice') && <td>{(u as any).practice || '-'}</td>}
      {visibleColumns.includes('grade') && <td>{(u as any).grade || '-'}</td>}
      {visibleColumns.includes('band') && <td>{(u as any).band || '-'}</td>}
      {visibleColumns.includes('mobile') && <td>{(u as any).mobile || '-'}</td>}
    </tr>
  );

  /* ── Render group node recursively ── */
  const renderGroupNode = (
    node: UserGroupNode,
    depth: number,
    parentOverrides: Record<string, any> = {},
  ): React.ReactNode => {
    const isExpanded = expandedGroups.has(node.key);
    const hasSubgroups = node.sub_groups && node.sub_groups.length > 0;
    const isLeaf = !hasSubgroups;
    const overrides = buildFilterOverrides(node, parentOverrides);
    const groupPath = node.key;

    const handleToggle = () => {
      toggleExpand(node.key);
      if (isLeaf && !isExpanded && !detailRows[groupPath]) {
        loadDetailRows(groupPath, overrides);
      }
    };

    const detailData = detailRows[groupPath];
    const isDetailLoading = detailLoading.has(groupPath);

    return (
      <React.Fragment key={node.key}>
        <tr
          className={`ge-group-header-row depth-${Math.min(depth, 3)}`}
          onClick={handleToggle}
          style={{ cursor: 'pointer' }}
        >
          <td colSpan={Math.max(visibleColumns.length - 1, 1)} style={{ paddingLeft: `${depth * 24 + 12}px` }}>
            <span className="ge-group-expand-icon">
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </span>
            <span className="ge-group-label">{node.label}</span>
            <span className="ge-group-count">({node.count})</span>
          </td>
          <td></td>
        </tr>

        {isExpanded && hasSubgroups && node.sub_groups!.map((sub) => renderGroupNode(sub, depth + 1, overrides))}

        {isExpanded && isLeaf && (
          <>
            {isDetailLoading && (
              <tr className="ge-detail-loading-row">
                <td colSpan={visibleColumns.length}>Loading...</td>
              </tr>
            )}
            {detailData && detailData.users.map((u) => renderUserRow(u))}
            {detailData && detailData.users.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="ge-empty-cell">No employees in this group</td>
              </tr>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  /* ── RENDER ── */
  return (
    <div className="grouped-employees-page">
      <div className="ge-page-header">
        <h1>{pageTitle}</h1>
      </div>

      {/* Toolbar */}
      <div className="ge-toolbar">
        <div className="ge-toolbar-left">
          {/* Active favorite chip */}
          {activeFavoriteName && (
            <span className="gt-fav-chip">
              <StarFilled className="gt-fav-chip-star" />
              {activeFavoriteName}
              <button
                className="gt-fav-chip-remove"
                onClick={() => {
                  setActiveFavoriteName(null);
                  setSearchChips([]);
                  setActiveGroupBy([]);
                  setDepartmentFilter('');
                  setManagerFilter('');
                  setRoleFilter('');
                  setCategoryFilter('');
                  setPracticeFilter('');
                  setActiveInactiveFilter('true');
                }}
                title="Remove favorite"
              >
                <X />
              </button>
            </span>
          )}

          {activeGroupBy.length > 0 && (
            <div className="ge-groupby-chips">
              <span className="ge-chip-icon">⊞</span>
              {activeGroupBy.map((field, idx) => (
                <React.Fragment key={field}>
                  {idx > 0 && <span className="ge-chip-separator">&gt;</span>}
                  <span className="ge-chip">
                    {getGroupByLabel(field)}
                    <button className="ge-chip-remove" onClick={() => removeGroupByField(field)} title="Remove">
                      <X />
                    </button>
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}

          <ColumnSearchBar
            fields={EMPLOYEE_SEARCH_FIELDS}
            chips={searchChips}
            onChipsChange={setSearchChips}
            placeholder="Search employees..."
          />
        </div>

        <div className="ge-toolbar-right">
          <Button
            variant={showFilters ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter />
            Filters
          </Button>

          <div className="ge-dropdown-container" ref={groupByMenuRef}>
            <Button variant="ghost" size="sm" onClick={() => setShowGroupByMenu(!showGroupByMenu)}>
              ⊞ Group By
            </Button>
            {showGroupByMenu && (
              <div className="ge-dropdown-menu ge-groupby-menu">
                {availableOptions.length === 0 ? (
                  <div className="ge-dropdown-empty">All fields selected</div>
                ) : (
                  availableOptions.map((opt) => (
                    <div key={opt.key} className="ge-dropdown-item-wrapper">
                      <button className="ge-dropdown-item" onClick={() => addGroupByField(opt.key)}>
                        {opt.label}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="ge-dropdown-container" ref={exportMenuRef}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportLoading}
            >
              <Download />
              Export
            </Button>
            {showExportMenu && (
              <div className="ge-dropdown-menu ge-export-menu">
                <button className="ge-dropdown-item" onClick={handleExport}>
                  <FileSpreadsheet />
                  Export as Excel
                </button>
              </div>
            )}
          </div>

          {/* Column Selector */}
          <div className="ge-dropdown-container" ref={columnMenuRef}>
            <Button variant="ghost" size="sm" onClick={() => setShowColumnMenu(!showColumnMenu)}>
              <Columns />
              Columns
            </Button>
            {showColumnMenu && (
              <div className="ge-dropdown-menu ge-column-menu">
                <div className="ge-column-header">
                  <span>Toggle Columns</span>
                  <div className="ge-column-header-actions">
                    <button onClick={handleSelectAllColumns}>All</button>
                    <button onClick={handleResetColumns}>Reset</button>
                  </div>
                </div>
                <div className="ge-column-body">
                  {EMPLOYEE_COLUMNS.map(col => (
                    <label key={col.key} className="ge-column-checkbox">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => handleColumnToggle(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <FavoritesDropdown
            pageKey="all_employees"
            getCurrentConfig={() => ({
              column_searches: searchChips.map(c => ({ field: c.field, label: c.label, value: c.value })),
              group_by: activeGroupBy as string[],
              visible_columns: visibleColumns,
              filters: {
                department_id: departmentFilter,
                manager_id: managerFilter,
                role: roleFilter,
                category: categoryFilter,
                practice: practiceFilter,
                active_inactive: activeInactiveFilter,
              },
            })}
            applyConfig={(cfg: FavoriteConfig) => {
              if (cfg.column_searches) {
                setSearchChips(cfg.column_searches.map(c => ({ id: Math.random().toString(36).slice(2, 11), ...c })));
              } else {
                setSearchChips([]);
              }
              setActiveGroupBy((cfg.group_by || []) as GroupByField[]);
              if (cfg.visible_columns && cfg.visible_columns.length > 0) {
                setVisibleColumns(cfg.visible_columns);
              }
              const f = cfg.filters || {};
              setDepartmentFilter(String(f.department_id ?? ''));
              setManagerFilter(String(f.manager_id ?? ''));
              setRoleFilter(String(f.role ?? ''));
              setCategoryFilter(String(f.category ?? ''));
              setPracticeFilter(String(f.practice ?? ''));
              setActiveInactiveFilter(String(f.active_inactive ?? 'true'));
            }}
            onActiveFavoriteChange={(name) => setActiveFavoriteName(name)}
          />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => (activeGroupBy.length > 0 ? fetchGroupedData() : fetchFlatData(1))}
            disabled={loading}
          >
            <Refresh className={loading ? 'spin' : ''} />
            Refresh
          </Button>

          <div className="ge-total-display">
            {activeGroupBy.length > 0 ? (
              <span><strong>{totalCount}</strong> employees</span>
            ) : (
              <span>
                {flatTotal > 0 && (
                  <>
                    <strong>{((flatPage - 1) * PER_PAGE) + 1}-{Math.min(flatPage * PER_PAGE, flatTotal)}</strong> of <strong>{flatTotal}</strong>
                  </>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="ge-filters-panel">
          <div className="ge-filters-row">
            <div className="ge-filter-group">
              <label>Department</label>
              <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
                <option value="">All Departments</option>
                {filterDepartments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="ge-filter-group">
              <label>Manager</label>
              <select value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)}>
                <option value="">All Managers</option>
                {filterManagers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="ge-filter-group">
              <label>Role</label>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="">All Roles</option>
                {filterRoles.map((r) => (
                  <option key={r.name} value={r.name}>{r.display_name}</option>
                ))}
              </select>
            </div>
            <div className="ge-filter-group">
              <label>Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="">All Categories</option>
                {filterCategories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="ge-filter-group">
              <label>Practice</label>
              <select value={practiceFilter} onChange={(e) => setPracticeFilter(e.target.value)}>
                <option value="">All Practices</option>
                {filterPractices.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="ge-filter-group">
              <label>Active/Inactive</label>
              <select value={activeInactiveFilter} onChange={(e) => setActiveInactiveFilter(e.target.value)}>
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div className="ge-filters-actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDepartmentFilter('');
                setManagerFilter('');
                setRoleFilter('');
                setCategoryFilter('');
                setPracticeFilter('');
                setActiveInactiveFilter('');
              }}
            >
              Clear All
            </Button>
          </div>
        </div>
      )}

      {error && <div className="ge-error">{error}</div>}

      {/* Scrollable Table Container */}
      <div className="ge-table-scroll-container">
        <div className="ge-table-wrapper">
        <table className="ge-table">
          <thead>
            <tr>
              {EMPLOYEE_COLUMNS.filter(col => visibleColumns.includes(col.key)).map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={visibleColumns.length} className="ge-loading-cell">Loading...</td>
              </tr>
            )}

            {!loading && activeGroupBy.length > 0 && groups.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="ge-empty-cell">No employees found</td>
              </tr>
            )}

            {!loading && activeGroupBy.length > 0 && groups.map((g) => renderGroupNode(g, 0))}

            {!loading && activeGroupBy.length === 0 && flatUsers.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length} className="ge-empty-cell">No employees found</td>
              </tr>
            )}

            {!loading && activeGroupBy.length === 0 && flatUsers.map((u) => renderUserRow(u))}
          </tbody>
        </table>
      </div>

      {/* Flat pagination */}
      {activeGroupBy.length === 0 && flatPages > 1 && (
        <div className="ge-pagination">
          <Button variant="ghost" size="sm" disabled={flatPage <= 1} onClick={() => fetchFlatData(flatPage - 1)}>
            Previous
          </Button>
          <span>Page {flatPage} of {flatPages}</span>
          <Button variant="ghost" size="sm" disabled={flatPage >= flatPages} onClick={() => fetchFlatData(flatPage + 1)}>
            Next
          </Button>
        </div>
      )}
      </div>
    </div>
  );
}

export default GroupedEmployees;
